import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useJournal } from "@/hooks/useJournal";
import { useState } from "react";

const Journal = () => {
  const { entries, isLoading, saveEntry } = useJournal();
  const [isSaving, setIsSaving] = useState(false);
  const [newEntry, setNewEntry] = useState({
    date: new Date().toISOString().split('T')[0],
    mood: "",
    emotions: "",
    skillsUsed: "",
    challenges: "",
    victories: "",
  });

  const handleChange = (field: keyof typeof newEntry) => (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    setNewEntry((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleMoodChange = (value: string) => {
    setNewEntry((prev) => ({ ...prev, mood: value }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await saveEntry(newEntry);
      // Reset form after successful save
      setNewEntry({
        date: new Date().toISOString().split('T')[0],
        mood: "",
        emotions: "",
        skillsUsed: "",
        challenges: "",
        victories: "",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8 pt-24">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-2xl font-bold mb-6">DBT Journal</h1>
          <Card className="p-4 md:p-6 mb-6">
            <div className="space-y-4 md:space-y-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="space-y-2">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-32 w-full" />
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 pt-24">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-2xl font-bold mb-6">DBT Journal</h1>
        
        <Card className="p-4 md:p-6 mb-6">
          <div className="space-y-4 md:space-y-6">
            <div className="space-y-2">
              <Label htmlFor="date">Date</Label>
              <Input
                type="date"
                id="date"
                value={newEntry.date}
                onChange={handleChange("date")}
                className="w-full"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="mood">Overall Mood</Label>
              <Select value={newEntry.mood} onValueChange={handleMoodChange}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select your mood" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Very Low</SelectItem>
                  <SelectItem value="2">Low</SelectItem>
                  <SelectItem value="3">Neutral</SelectItem>
                  <SelectItem value="4">Good</SelectItem>
                  <SelectItem value="5">Very Good</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="emotions">Emotions Experienced</Label>
              <Textarea
                id="emotions"
                placeholder="What emotions did you experience today?"
                value={newEntry.emotions}
                onChange={handleChange("emotions")}
                className="min-h-[100px]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="skillsUsed">DBT Skills Used</Label>
              <Textarea
                id="skillsUsed"
                placeholder="What DBT skills did you practice today?"
                value={newEntry.skillsUsed}
                onChange={handleChange("skillsUsed")}
                className="min-h-[100px]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="challenges">Challenges</Label>
              <Textarea
                id="challenges"
                placeholder="What challenges did you face?"
                value={newEntry.challenges}
                onChange={handleChange("challenges")}
                className="min-h-[100px]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="victories">Victories</Label>
              <Textarea
                id="victories"
                placeholder="What went well today?"
                value={newEntry.victories}
                onChange={handleChange("victories")}
                className="min-h-[100px]"
              />
            </div>

            <Button onClick={handleSave} className="w-full" disabled={isSaving}>
              {isSaving ? "Saving..." : "Save Journal Entry"}
            </Button>
          </div>
        </Card>

        {entries.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">Previous Entries</h2>
            {entries.map((entry, index) => (
              <Card key={index} className="p-4">
                <div className="space-y-2">
                  <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-2">
                    <p className="font-medium">Date: {entry.date}</p>
                    <p className="text-sm">Mood: {entry.mood}</p>
                  </div>
                  {entry.emotions && (
                    <div>
                      <Label>Emotions</Label>
                      <p className="text-sm mt-1">{entry.emotions}</p>
                    </div>
                  )}
                  {entry.skillsUsed && (
                    <div>
                      <Label>Skills Used</Label>
                      <p className="text-sm mt-1">{entry.skillsUsed}</p>
                    </div>
                  )}
                  {entry.challenges && (
                    <div>
                      <Label>Challenges</Label>
                      <p className="text-sm mt-1">{entry.challenges}</p>
                    </div>
                  )}
                  {entry.victories && (
                    <div>
                      <Label>Victories</Label>
                      <p className="text-sm mt-1">{entry.victories}</p>
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Journal;